import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { enrollCourseApi, getMyEnrollmentsApi } from '../api/apiServices';

const ProgressContext = createContext(null);
const ENROLLMENTS_KEY = 'learnflow_enrollments';

export function ProgressProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [enrollments, setEnrollments] = useState([]);

  // Load enrollments when user changes
  useEffect(() => {
    const loadEnrollments = async () => {
      // Load from localStorage first
      const stored = localStorage.getItem(ENROLLMENTS_KEY);
      if (stored) {
        try {
          setEnrollments(JSON.parse(stored));
        } catch (e) {
          console.error("Failed to parse local enrollments");
        }
      }

      // Sync with backend if user is logged in
      if (user && isAuthenticated) {
        try {
          const res = await getMyEnrollmentsApi();
          if (res.success && Array.isArray(res.enrollments)) {
            setEnrollments(res.enrollments);
            localStorage.setItem(ENROLLMENTS_KEY, JSON.stringify(res.enrollments));
          }
        } catch (err) {
          console.error("Failed to sync enrollments from backend:", err);
        }
      }
    };

    loadEnrollments();
  }, [user, isAuthenticated]);

  const saveToLocal = useCallback((data) => {
    setEnrollments(data);
    localStorage.setItem(ENROLLMENTS_KEY, JSON.stringify(data));
  }, []);

  const getEnrollment = useCallback((courseId) => {
    if (!courseId || !user) return null;
    const targetId = String(courseId).trim();

    return enrollments.find(e => {
      const storedCourseId = e.courseId?._id 
        ? String(e.courseId._id).trim()
        : String(e.courseId).trim();
      return storedCourseId === targetId && 
             String(e.userId || e.user?._id).trim() === String(user.id || user._id);
    });
  }, [enrollments, user]);

  const isEnrolled = useCallback((courseId) => {
    return !!getEnrollment(courseId);
  }, [getEnrollment]);


  const enroll = useCallback(async (courseId) => {
    if (!user || !isAuthenticated) return;
    if (isEnrolled(courseId)) return;

    try {
      const result = await enrollCourseApi(courseId);

      if (result.success) {
        const newEnrollment = {
          courseId: String(courseId),
          userId: String(user.id),
          enrolledAt: new Date().toISOString(),
          completedLessons: [],
          quizResults: [],
          courseCompleted: false,
          lastLessonId: null,
          ...result.enrollment,
        };

        saveToLocal([...enrollments, newEnrollment]);
      }
    } catch (err) {
      console.error("Enroll failed:", err);
      // If backend says already enrolled, force sync
      if (err.response?.data?.message?.toLowerCase().includes("already enrolled")) {
        try {
          const res = await getMyEnrollmentsApi();
          if (res.success && res.enrollments) {
            saveToLocal(res.enrollments);
          }
        } catch (syncErr) {}
      }
      throw err;
    }
  }, [user, isAuthenticated, isEnrolled, enrollments, saveToLocal]);


  
  // ==================== UPDATED / NEW FUNCTIONS ====================

  const completeLesson = useCallback((courseId, lessonId) => {
    if (!user) return;
    const updated = enrollments.map(e => {
      if (String(e.courseId) === String(courseId) && String(e.userId) === String(user.id)) {
        if (e.completedLessons.includes(lessonId)) return e;
        return { 
          ...e, 
          completedLessons: [...e.completedLessons, lessonId],
          lastLessonId: lessonId 
        };
      }
      return e;
    });
    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  const isLessonCompleted = useCallback((courseId, lessonId) => {
    const e = getEnrollment(courseId);
    return e ? e.completedLessons.includes(lessonId) : false;
  }, [getEnrollment]);

  const saveQuizResult = useCallback((result) => {
    if (!user || !result) return;
    const updated = enrollments.map(e => {
      if (String(e.courseId) === String(result.courseId) && String(e.userId) === String(user.id)) {
        const existingIndex = e.quizResults.findIndex(r => 
          r.quizId === result.quizId || r.lessonId === result.lessonId
        );
        const newResults = existingIndex >= 0 
          ? e.quizResults.map((r, i) => i === existingIndex ? result : r)
          : [...e.quizResults, result];
        return { ...e, quizResults: newResults };
      }
      return e;
    });
    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  const getQuizResult = useCallback((quizOrLessonId) => {
    for (const e of enrollments) {
      const r = e.quizResults.find(r => 
        r.quizId === quizOrLessonId || r.lessonId === quizOrLessonId
      );
      if (r) return r;
    }
    return undefined;
  }, [enrollments]);

  const setLastLesson = useCallback((courseId, lessonId) => {
    if (!user) return;
    const updated = enrollments.map(e =>
      String(e.courseId) === String(courseId) && String(e.userId) === String(user.id)
        ? { ...e, lastLessonId: lessonId }
        : e
    );
    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  const completeCourse = useCallback((courseId) => {
    if (!user) return;
    const updated = enrollments.map(e =>
      String(e.courseId) === String(courseId) && String(e.userId) === String(user.id)
        ? { 
            ...e, 
            courseCompleted: true, 
            completedAt: new Date().toISOString() 
          }
        : e
    );
    saveToLocal(updated);
  }, [enrollments, user, saveToLocal]);

  const getLessonProgress = useCallback((courseId, totalLessons) => {
    const e = getEnrollment(courseId);
    if (!e || totalLessons === 0) return 0;
    return Math.round((e.completedLessons.length / totalLessons) * 100);
  }, [getEnrollment]);

  // New helper: Check if all previous lessons' quizzes are done up to current lesson
  const canAccessLesson = useCallback((courseId, targetLessonId) => {
    const e = getEnrollment(courseId);
    if (!e) return false;

    const course = /* you can pass course data or fetch it */ null; // We'll handle this in component

    // For now, we'll implement the check in the component using getQuizResult
    return true; // placeholder - main logic will be in LessonViewerPage
  }, [getEnrollment]);

  return (
    <ProgressContext.Provider value={{
      enroll,
      isEnrolled,
      completeLesson,
      isLessonCompleted,
      saveQuizResult,
      getQuizResult,
      getEnrollment,
      setLastLesson,
      completeCourse,
      getLessonProgress,
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within ProgressProvider');
  return ctx;
}